/**
 * Form-submission statistical trend analysis.
 *
 * Moved out of the frontend `utils/trendAnalysis.ts` into the shared package so
 * all three surfaces draw from one implementation: the per-resident panel
 * (client-side), the nightly sweep, and the server-computed
 * GET /api/facilities/[id]/form-trends endpoint. Sibling of
 * `adlTrendAnalysis.ts`.
 *
 * No Prisma / AWS / Node / Next imports — pure TypeScript only. Submissions are
 * loosely typed (`Record<string, unknown>`); callers shape backend rows so that
 * `form_template.name` / `form_template.schema` / `submission_date` / `data`
 * are present (the backend relation is `form_templates`, so map it first).
 */

import {
  calculateStatistics,
  calculateZScore,
  isOutlierIQR,
  extractNumericFieldLabels,
  type TrendAlert,
} from "./trendAnalysis";

// ── Private helpers ────────────────────────────────────────────────────────────

function analyzeField(
  fieldId: string,
  fieldLabel: string,
  values: number[],
  formName: string,
): TrendAlert[] {
  const alerts: TrendAlert[] = [];

  if (values.length < 3) return alerts;

  const stats = calculateStatistics(values);
  const currentValue = values[values.length - 1];
  if (currentValue === undefined) return alerts;
  const zScore = calculateZScore(currentValue, stats.mean, stats.stdDev);

  const isOutlier =
    Math.abs(zScore) > 2.5 ||
    isOutlierIQR(currentValue, stats.q1, stats.q3, stats.iqr);

  if (isOutlier && values.length >= 5) {
    const direction = currentValue > stats.mean ? "spike" : "drop";
    const severity: "info" | "warning" | "critical" =
      Math.abs(zScore) > 3
        ? "critical"
        : Math.abs(zScore) > 2.5
          ? "warning"
          : "info";

    alerts.push({
      id: `outlier-${fieldId}-${Date.now()}`,
      residentId: "",
      residentName: "",
      severity,
      type: direction === "drop" ? "sudden_drop" : "outlier",
      fieldName: fieldLabel,
      formName,
      message: `Unusual ${direction === "spike" ? "increase" : "drop"} in ${fieldLabel}`,
      details: `Current value (${currentValue.toFixed(1)}) is ${Math.abs(zScore).toFixed(1)} standard deviations from baseline (${stats.mean.toFixed(1)})`,
      currentValue,
      baselineValue: stats.mean,
      zScore,
      changePercentage: ((currentValue - stats.mean) / stats.mean) * 100,
      detectedAt: new Date(),
      dataPoints: stats.count,
    });
  }

  if (stats.trend === "declining" && values.length >= 5) {
    const recentChange =
      ((stats.recentMean - stats.overallMean) / stats.overallMean) * 100;

    if (recentChange < -15) {
      const severity: "info" | "warning" | "critical" =
        recentChange < -30 ? "critical" : "warning";

      alerts.push({
        id: `declining-${fieldId}-${Date.now()}`,
        residentId: "",
        residentName: "",
        severity,
        type: "declining_trend",
        fieldName: fieldLabel,
        formName,
        message: `${fieldLabel} showing sustained decline`,
        details: `Recent average (${stats.recentMean.toFixed(1)}) is ${Math.abs(recentChange).toFixed(1)}% below historical baseline (${stats.overallMean.toFixed(1)})`,
        currentValue: stats.recentMean,
        baselineValue: stats.overallMean,
        changePercentage: recentChange,
        detectedAt: new Date(),
        dataPoints: stats.count,
      });
    }
  }

  if (values.length >= 10) {
    const recentValues = values.slice(-7);
    const recentStats = calculateStatistics(recentValues);
    const varianceRatio = recentStats.stdDev / stats.stdDev;

    if (varianceRatio > 1.5 && stats.stdDev > 0) {
      alerts.push({
        id: `variance-${fieldId}-${Date.now()}`,
        residentId: "",
        residentName: "",
        severity: "info",
        type: "increased_variance",
        fieldName: fieldLabel,
        formName,
        message: `${fieldLabel} readings are more inconsistent than usual`,
        details: `Recent measurements show ${(varianceRatio * 100 - 100).toFixed(0)}% more variation than usual (σ: ${stats.stdDev.toFixed(1)} → ${recentStats.stdDev.toFixed(1)})`,
        currentValue: recentStats.stdDev,
        baselineValue: stats.stdDev,
        detectedAt: new Date(),
        dataPoints: stats.count,
      });
    }
  }

  return alerts;
}

function extractNumericFields(
  submissions: Record<string, unknown>[],
): Map<string, { label: string; values: number[]; dates: Date[] }> {
  const fieldData = new Map<
    string,
    { label: string; values: number[]; dates: Date[] }
  >();

  submissions.forEach((submission) => {
    const formTemplate = submission["form_template"] as
      | Record<string, unknown>
      | undefined;
    const schema = formTemplate?.["schema"];
    const data = (submission["data"] || {}) as Record<string, unknown>;
    const date = new Date(submission["submission_date"] as string);

    const fieldLabels = extractNumericFieldLabels(schema);

    Object.entries(data).forEach(([fieldId, value]) => {
      if (typeof value === "number" && !isNaN(value)) {
        const label = fieldLabels.get(fieldId) || fieldId;
        if (!fieldData.has(fieldId)) {
          fieldData.set(fieldId, { label, values: [], dates: [] });
        }
        const field = fieldData.get(fieldId)!;
        field.values.push(value);
        field.dates.push(date);
      }
    });
  });

  return fieldData;
}

// ── Public API ──────────────────────────────────────────────────────────────────

type FormSubmission = Record<string, unknown>;

/**
 * Analyze a single resident's form submissions for concerning statistical
 * trends. (Exposed in the frontend as `analyzeResidentTrends` for backward
 * compatibility.)
 */
export function analyzeFormSubmissionTrends(
  residentId: string,
  residentName: string,
  formSubmissions: FormSubmission[],
): TrendAlert[] {
  const alerts: TrendAlert[] = [];

  if (!formSubmissions || formSubmissions.length < 3) return alerts;

  const groupedByForm = formSubmissions.reduce<
    Record<string, { formName: string; submissions: FormSubmission[] }>
  >((acc, submission) => {
    const formId = submission["form_template_id"] as string;
    const formName =
      ((submission["form_template"] as Record<string, unknown>)?.[
        "name"
      ] as string) || "Unknown Form";
    if (!acc[formId]) acc[formId] = { formName, submissions: [] };
    acc[formId].submissions.push(submission);
    return acc;
  }, {});

  Object.values(groupedByForm).forEach(({ formName, submissions }) => {
    const sorted = [...submissions].sort(
      (a, b) =>
        new Date(a["submission_date"] as string).getTime() -
        new Date(b["submission_date"] as string).getTime(),
    );

    const fieldData = extractNumericFields(sorted);

    fieldData.forEach((data, fieldId) => {
      const fieldAlerts = analyzeField(
        fieldId,
        data.label,
        data.values,
        formName,
      );
      fieldAlerts.forEach((alert) => {
        alerts.push({
          ...alert,
          id: `${residentId}-${alert.id}`,
          residentId,
          residentName,
        });
      });
    });
  });

  const severityOrder = { critical: 0, warning: 1, info: 2 };
  alerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return alerts;
}
